import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";

import {
  type HcfTimeclockComplianceVideoConfig,
  type ShiftStateData,
  type ShiftStateDependencies,
  shiftStateSchema,
} from "../types";

interface GetShiftStateParams {
  shiftStateDependencies?: Pick<ShiftStateDependencies, "_id">;
}

export const DEFAULT_MESH_TIME_TO_ALLOW_SKIP_IN_SEC = 300;
export const DEFAULT_MESH_AGENCY_ID = 1779;
export const DEFAULT_MESH_AGENCY_NAME = "clipboard";
export const DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG: HcfTimeclockComplianceVideoConfig = {
  maxVideoLength: 60,
  minVideoLength: 8,
  videoBitRate: 1_000_000,
  videoHeight: 736,
  videoWidth: 414,
};

export function getShiftStateEndpointUrl(shiftId: string) {
  return `/shift/${shiftId}/state`;
}

export function useGetShiftState(
  params: GetShiftStateParams,
  options?: UseQueryOptions<ShiftStateData>
): UseQueryResult<ShiftStateData> {
  const { shiftStateDependencies } = params;
  const isQueryEnabled = isDefined(shiftStateDependencies);

  let useQueryOptions: UseQueryOptions<ShiftStateData> = {
    enabled: false,
  };

  if (isQueryEnabled) {
    const shiftStateEndpointUrl = getShiftStateEndpointUrl(shiftStateDependencies._id);

    useQueryOptions = {
      queryKey: [shiftStateEndpointUrl, shiftStateDependencies],
      queryFn: async () => {
        const response = await get({
          url: shiftStateEndpointUrl,
          responseSchema: shiftStateSchema,
        });
        return response.data;
      },
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.GET_SHIFT_STATE_FAILURE,
      },
      ...options,
    };
  }

  return useQuery(useQueryOptions);
}
